<template>
  <button
    v-bind="$attrs"
    :aria-busy="loading || undefined"
    data-test-id="ui-button"
    class="ui-button"
    :class="[
      `ui-button--${resolvedColor}`,
      `ui-button--${resolvedVariant}`,
      `ui-button--${size}`,
      { 'ui-button--icon-only': iconOnly, 'ui-button--loading': loading },
    ]"
    :disabled="disabled || loading"
    :type="type"
  >
    <span class="ui-button__content">
      <span v-if="$slots.leftIcon" class="ui-button__icon"><slot name="leftIcon" /> </span>
      <slot />
      <span v-if="$slots.rightIcon" class="ui-button__icon"><slot name="rightIcon" /> </span>
    </span>
    <span class="ui-button__loader">
      <span v-if="loading" aria-hidden="true" class="ui-button__loader-icon" />
    </span>
  </button>
</template>

<script setup lang="ts">
  import { computed, inject, useSlots } from 'vue'

  import { type ButtonColor, type ButtonVariant, buttonGroupKey } from './context'

  defineOptions({ inheritAttrs: false, name: 'UiButton' })

  const props = withDefaults(
    defineProps<{
      color?: ButtonColor
      disabled?: boolean
      loading?: boolean
      size?: '2xl' | 'lg' | 'md' | 'sm' | 'xl' | 'xs'
      type?: 'button' | 'reset' | 'submit'
      variant?: ButtonVariant
    }>(),
    { loading: false, size: 'md', type: 'button' },
  )

  const defaults = inject(buttonGroupKey, {})
  const slots = useSlots()
  const resolvedColor = computed(() => props.color ?? defaults.color ?? 'brand')
  const resolvedVariant = computed(() => props.variant ?? defaults.variant ?? 'primary')
  const iconOnly = computed(() => !slots.default)
</script>

<style scoped lang="scss">
@use '@repo/ui/helpers.scss' as helpers;
@use '@repo/ui/typography.scss' as typography;

/* Focus and disabled states intentionally take precedence over hover. */
/* stylelint-disable no-descending-specificity */

.ui-button {
  --btn-bg: var(--color-brand-600);
  --btn-bg-focus: var(--color-brand-600);
  --btn-bg-hover: var(--color-brand-700);
  --btn-border: transparent;
  --btn-color: var(--color-base-white);
  --btn-color-hover: var(--color-base-white);
  --btn-ring: var(--color-brand-500);
  --btn-ring-border: var(--color-brand-300);
  --icon-size: 16px;

  align-items: center;
  background: var(--btn-bg);
  border: 0;
  box-shadow: inset 0 0 0 1px var(--btn-border);
  color: var(--btn-color);
  cursor: pointer;
  display: grid;
  grid-template-areas: 'stack';
  justify-content: center;
  outline: 2px solid transparent;
  outline-offset: 2px;
  transition: all 0.2s ease-in-out;
}

.ui-button:hover:not(:disabled),
.ui-button[data-ui-state='hover']:not(:disabled) {
  background: var(--btn-bg-hover);
  color: var(--btn-color-hover);
}

.ui-button:active:not(:disabled),
.ui-button[data-ui-state='pressed']:not(:disabled) {
  background: var(--btn-bg-hover);
  color: var(--btn-color-hover);
}

.ui-button:focus-visible,
.ui-button[data-ui-state='focus'] {
  background: var(--btn-bg-focus);
  box-shadow: inset 0 0 0 1px var(--btn-ring-border);
  outline: 2px solid var(--btn-ring) !important;
  outline-offset: 2px;
}

.ui-button:disabled:not(.ui-button--loading) {
  color: var(--color-gray-light-400);
  cursor: default;
}

.ui-button:disabled:not(.ui-button--loading, .ui-button--icon-only) {
  background: var(--color-gray-light-100);
  box-shadow: inset 0 0 0 1px transparent;
}

.ui-button--gray {
  --btn-ring: var(--color-gray-light-500);
  --btn-ring-border: var(--color-gray-light-300);
}

.ui-button--purple {
  --btn-ring: var(--color-purple-500);
  --btn-ring-border: var(--color-purple-300);
}

.ui-button--error {
  --btn-ring: var(--color-error-600);
  --btn-ring-border: var(--color-error-300);
}

.ui-button--gray.ui-button--primary {
  --btn-bg: var(--color-base-white);
  --btn-bg-focus: var(--color-base-white);
  --btn-bg-hover: var(--color-gray-light-50);
  --btn-border: var(--color-gray-light-300);
  --btn-color: var(--color-gray-light-700);
  --btn-color-hover: var(--color-gray-light-800);
}

.ui-button--gray.ui-button--secondary {
  --btn-bg: var(--color-base-white);
  --btn-bg-focus: var(--color-base-white);
  --btn-bg-hover: var(--color-gray-light-50);
  --btn-border: var(--color-border-border-primary);
  --btn-color: var(--color-gray-light-700);
  --btn-color-hover: var(--color-gray-light-800);
}

.ui-button--gray.ui-button--tertiary {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: var(--color-gray-light-50);
  --btn-border: transparent;
  --btn-color: var(--color-gray-light-700);
  --btn-color-hover: var(--color-gray-light-800);
}

.ui-button--gray.ui-button--text,
.ui-button--gray.ui-button--table {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: transparent;
  --btn-border: transparent;
  --btn-color: var(--color-gray-light-700);
  --btn-color-hover: var(--color-gray-light-800);
}

.ui-button--brand.ui-button--secondary {
  --btn-bg: var(--color-base-white);
  --btn-bg-focus: var(--color-base-white);
  --btn-bg-hover: var(--color-brand-50);
  --btn-border: var(--color-border-border-brand-subtle);
  --btn-color: var(--color-brand-700);
  --btn-color-hover: var(--color-brand-800);
}

.ui-button--brand.ui-button--tertiary {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: var(--color-brand-50);
  --btn-border: transparent;
  --btn-color: var(--color-brand-700);
  --btn-color-hover: var(--color-brand-800);
}

.ui-button--brand.ui-button--text {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: transparent;
  --btn-border: transparent;
  --btn-color: var(--color-brand-700);
  --btn-color-hover: var(--color-brand-800);
}

.ui-button--brand.ui-button--table {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: transparent;
  --btn-border: transparent;
  --btn-color: var(--color-tertiary-600);
  --btn-color-hover: var(--color-foreground-fg-brand-primary-alt);
}

.ui-button--purple.ui-button--primary {
  --btn-bg: var(--color-purple-600);
  --btn-bg-focus: var(--color-purple-600);
  --btn-bg-hover: var(--color-purple-700);
  --btn-border: transparent;
  --btn-color: var(--color-base-white);
  --btn-color-hover: var(--color-base-white);
}

.ui-button--purple.ui-button--secondary {
  --btn-bg: var(--color-base-white);
  --btn-bg-focus: var(--color-base-white);
  --btn-bg-hover: var(--color-purple-50);
  --btn-border: var(--color-purple-300);
  --btn-color: var(--color-purple-700);
  --btn-color-hover: var(--color-purple-800);
}

.ui-button--purple.ui-button--tertiary {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: var(--color-purple-50);
  --btn-border: transparent;
  --btn-color: var(--color-purple-700);
  --btn-color-hover: var(--color-purple-800);
}

.ui-button--purple.ui-button--text,
.ui-button--purple.ui-button--table {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: transparent;
  --btn-border: transparent;
  --btn-color: var(--color-purple-700);
  --btn-color-hover: var(--color-purple-800);
}

.ui-button--error.ui-button--primary {
  --btn-bg: var(--color-error-600);
  --btn-bg-focus: var(--color-error-600);
  --btn-bg-hover: var(--color-error-700);
  --btn-border: transparent;
  --btn-color: var(--color-base-white);
  --btn-color-hover: var(--color-base-white);
}

.ui-button--error.ui-button--secondary {
  --btn-bg: var(--color-base-white);
  --btn-bg-focus: var(--color-base-white);
  --btn-bg-hover: var(--color-error-50);
  --btn-border: var(--color-error-300);
  --btn-color: var(--color-error-700);
  --btn-color-hover: var(--color-error-800);
}

.ui-button--error.ui-button--tertiary {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: var(--color-error-50);
  --btn-border: transparent;
  --btn-color: var(--color-error-700);
  --btn-color-hover: var(--color-error-800);
}

.ui-button--error.ui-button--text,
.ui-button--error.ui-button--table {
  --btn-bg: transparent;
  --btn-bg-focus: transparent;
  --btn-bg-hover: transparent;
  --btn-border: transparent;
  --btn-color: var(--color-error-500);
  --btn-color-hover: var(--color-error-800);
}

.ui-button--text {
  background: transparent !important;
  border-radius: var(--radius-xs) !important;
  padding: 0 !important;
}

.ui-button--table {
  background: transparent !important;
  border-radius: var(--radius-md) !important;
  font-weight: 400 !important;
  padding: 0 !important;
}

.ui-button--2xl {
  @include typography.font-style('text-lg', 'semibold');

  --icon-size: 24px;

  border-radius: var(--radius-lg);
  padding: 16px 22px;
}

.ui-button--xl {
  @include typography.font-style('text-md', 'semibold');

  --icon-size: 20px;

  border-radius: var(--radius-md);
  padding: 12px 18px;
}

.ui-button--lg {
  @include typography.font-style('text-md', 'semibold');

  --icon-size: 20px;

  border-radius: var(--radius-md);
  padding: 10px 16px;
}

.ui-button--md {
  @include typography.font-style('text-sm', 'semibold');

  --icon-size: 20px;

  border-radius: var(--radius-md);
  padding: 10px 14px;
}

.ui-button--sm {
  @include typography.font-style('text-sm', 'semibold');

  --icon-size: 20px;

  border-radius: var(--radius-md);
  padding: 8px 12px;
}

.ui-button--xs {
  @include typography.font-style('text-xs', 'semibold');

  --icon-size: 16px;

  border-radius: var(--radius-md);
  padding: 8px;
}

.ui-button--icon-only {
  padding: 0;
}

.ui-button--icon-only.ui-button--2xl {
  @include helpers.square(56px);
}

.ui-button--icon-only.ui-button--xl {
  @include helpers.square(48px);
}

.ui-button--icon-only.ui-button--lg {
  @include helpers.square(44px);
}

.ui-button--icon-only.ui-button--md {
  @include helpers.square(40px);
}

.ui-button--icon-only.ui-button--sm {
  @include helpers.square(36px);
}

.ui-button--icon-only.ui-button--xs {
  @include helpers.square(32px);
}

.ui-button__content,
.ui-button__loader {
  align-items: center;
  display: inline-flex;
  grid-area: stack;
}

.ui-button__content {
  gap: 8px;
}

.ui-button__loader {
  justify-content: center;
  visibility: hidden;
}

.ui-button__icon {
  align-items: center;
  display: inline-flex;
  justify-content: center;
}

.ui-button:not(.ui-button--icon-only) .ui-button__icon {
  height: var(--icon-size);
  width: var(--icon-size);
}

.ui-button:not(.ui-button--icon-only) .ui-button__icon :deep(svg) {
  display: block;
  height: var(--icon-size);
  width: var(--icon-size);
}

.ui-button--loading .ui-button__content {
  visibility: hidden;
}

.ui-button--loading .ui-button__loader {
  visibility: visible;
}

.ui-button__loader-icon {
  animation: button-spin 1s linear infinite;
  border: 2px solid var(--btn-color);
  border-radius: 50%;
  border-top-color: var(--btn-bg-hover);
  height: var(--icon-size);
  width: var(--icon-size);
}

@keyframes button-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
